import { AgGridTable } from 'components/AgGrid/AgGridTable';
import { ButtonDeleteIds } from 'components/ButtonDeleteIds';
import { DeleteItemFrom } from 'components/DeleteItemFrom';
import { TableTopPanel } from 'components/TableTopPanel';
import { findAllConstructionTypesFromObjects } from 'components/WorkOrderConstructionTypesTabSelect';
import type { WorkOrderChangeObjectItem, WorkOrderChangeOtherPriceData, WorkOrderChangeSeasonData } from 'gql/graphql';
import type { IUseAccess } from 'hooks/useAccess';
import { destroyAgGridTableFilters } from 'utils';

import { forwardRef, useCallback, useEffect, useMemo } from 'react';

import { getCellClass, getRowClass } from '../CellRenderers';
import {
    excludeTitleModalCancelBtnText,
    excludeTitleModalConfirmBtnText,
    excludeTitleModalConfirmText,
    excludeTitleModalTitle,
    removeTitleModalConfirmText,
    removeTitleModalTitle,
} from '../constants';
import { EditNoteModal } from '../EditNoteModal';
import { EditWorkOrderChangeOtherDialog } from '../EditWorkOrderChangeOtherDialog';
import { EditWorkOrderChangeOtherDistribution } from '../EditWorkOrderChangeOtherDistribution';
import {
    useDeleteOthersFromWorkOrderChange,
    useWorkOrderChangeOtherColumns,
    useWorkOrderChangeOtherPriceList,
} from '../hooks';

const defaultColDef = {
    cellClassRules: getCellClass,
};

interface IWorkOrderChangeOtherPrice {
    workOrderChangeId: number;
    constructionTypeId: number;
    contractId: number;
    workOrderObjectId: number | undefined;
    workOrderChangeObjectId: number | undefined;
    createUpdateFn?: (fn: () => void) => void;
    workOrderChangeObjectsData: WorkOrderChangeObjectItem[];
    isActionsDisabled: boolean;
    workOrderChangeUerRules: IUseAccess['workOrderChangeUer'];
    costRounding: number;
    workOrderChangeSeasons: WorkOrderChangeSeasonData[];
}

export const WorkOrderChangeOtherPrice = forwardRef((props: IWorkOrderChangeOtherPrice, ref: any) => {
    const {
        workOrderChangeId,
        workOrderObjectId,
        workOrderChangeObjectId,
        contractId,
        createUpdateFn,
        constructionTypeId,
        workOrderChangeObjectsData,
        isActionsDisabled,
        workOrderChangeUerRules,
        costRounding,
        workOrderChangeSeasons,
    } = props;

    const tableKey = useMemo(
        () => `others_${workOrderObjectId}_${workOrderChangeObjectId}`,
        [workOrderChangeObjectId, workOrderObjectId],
    );

    const { tableItems, totalItems, fetchedItems, handleGetWorkOrderChangeOtherPrice, changeFilterHandler } =
        useWorkOrderChangeOtherPriceList({
            workOrderChangeId,
            workOrderObjectId,
            workOrderChangeObjectId,
        });

    useEffect(() => {
        if (handleGetWorkOrderChangeOtherPrice) {
            createUpdateFn?.(handleGetWorkOrderChangeOtherPrice);
        }
    }, [createUpdateFn, handleGetWorkOrderChangeOtherPrice]);

    const resetTableFiltersData = useCallback(() => {
        destroyAgGridTableFilters({
            api: ref.current?.api,
            columns: ['name', 'status'],
        });
    }, [ref]);

    useEffect(() => {
        resetTableFiltersData();
    }, [resetTableFiltersData, workOrderObjectId]);

    const refreshWithFilterReset = useCallback(() => {
        handleGetWorkOrderChangeOtherPrice();
        resetTableFiltersData();
    }, [handleGetWorkOrderChangeOtherPrice, resetTableFiltersData]);

    const {
        fullSetColumns,
        resetCheckedRows,
        checkedRowsArr,
        checkedOtherRow,
        isSingleDeleteModalOpened,
        closeSingleDeleteModalHandler,
        isEditDistributionOpened,
        closeEditDistributionModalHandler,
        fullSetSettingsColumns,
        handleTableSettingsConfirm,
        isColumnVisible,
        isEditOtherOpened,
        isEditNoteModalOpened,
        isViewOtherModalOpened,
        closeViewOtherModalHandler,
        closeEditOtherModalHandler,
        closeEditNoteModalHandler,
        rowData,
    } = useWorkOrderChangeOtherColumns({
        workOrderChangeId,
        isActionsDisabled,
        workOrderObjectId,
        workOrderChangeObjectId,
        gridApiRef: ref.current,
        changeFilterHandler,
        workOrderChangeUerRules,
        costRounding,
        workOrderChangeSeasons,
    });

    const { deleteData, deleteStatus, deleteError, deleteSetData, handleDelete, onSuccess } =
        useDeleteOthersFromWorkOrderChange({
            workOrderChangeId,
            checkedRowsArr,
            refreshTable: refreshWithFilterReset,
            resetCheckedRows,
        });

    const singleDeleteSuccess = useCallback(() => {
        onSuccess?.();
        closeSingleDeleteModalHandler();
    }, [closeSingleDeleteModalHandler, onSuccess]);

    const { selectedWorkOrderChangeOtherPriceId, selectedWorkOrderOtherPriceId } = useMemo(() => {
        const { workOrderChangeOtherPriceId, workOrderOtherPriceId } =
            (checkedRowsArr?.[0] as Partial<WorkOrderChangeOtherPriceData>) || {};

        return {
            selectedWorkOrderChangeOtherPriceId: workOrderChangeOtherPriceId,
            selectedWorkOrderOtherPriceId: workOrderOtherPriceId,
        };
    }, [checkedRowsArr]);

    const { contractOtherId: selectedContractOtherId } = checkedOtherRow || {};

    const { removeModalTitle, removeModalText, removeModalConfirmBtn, removeModalCancelBtn } = useMemo(() => {
        if (checkedOtherRow?.hasAcceptedCost === false) {
            return {
                removeModalTitle: excludeTitleModalTitle,
                removeModalText: excludeTitleModalConfirmText,
                removeModalConfirmBtn: excludeTitleModalConfirmBtnText,
                removeModalCancelBtn: excludeTitleModalCancelBtnText,
            };
        }

        return {
            removeModalTitle: removeTitleModalTitle,
            removeModalText: removeTitleModalConfirmText,
        };
    }, [checkedOtherRow?.hasAcceptedCost]);

    const constructionTypes = useMemo(
        () => findAllConstructionTypesFromObjects({ workOrderChangeObjectsData }),
        [workOrderChangeObjectsData],
    );

    return (
        <>
            <TableTopPanel
                checkedRowsArr={checkedRowsArr}
                resetCheckedRows={resetCheckedRows}
                totalItems={totalItems}
                fetchedItems={fetchedItems}
                optionalColumns={fullSetSettingsColumns}
                handleTableSettingsConfirm={handleTableSettingsConfirm}
                isColumnVisible={isColumnVisible}
                showPinColumn
                tableApi={ref?.current}
            >
                <ButtonDeleteIds
                    deleteData={deleteData}
                    deleteStatus={deleteStatus}
                    deleteError={deleteError}
                    deleteSetData={deleteSetData}
                    handleDelete={handleDelete}
                    onSuccess={onSuccess}
                />
            </TableTopPanel>

            <div style={{ height: '100%' }} className="ag-theme-alpine WorkOrderChangePrice">
                <AgGridTable
                    rowData={tableItems}
                    animateRows
                    rowSelection="multiple"
                    suppressRowClickSelection
                    columnDefs={fullSetColumns}
                    defaultColDef={defaultColDef}
                    rowClassRules={getRowClass}
                    ref={ref}
                    context={{
                        tableUniqueId: tableKey,
                    }}
                />
            </div>

            {(isEditOtherOpened || isViewOtherModalOpened) && (
                <EditWorkOrderChangeOtherDialog
                    isOpened={isEditOtherOpened || isViewOtherModalOpened}
                    closeModalHandler={isViewOtherModalOpened ? closeViewOtherModalHandler : closeEditOtherModalHandler}
                    contractId={contractId}
                    entryId={selectedContractOtherId}
                    workOrderOtherPriceId={selectedContractOtherId}
                    currentConstructionTypeId={constructionTypeId}
                    constructionTypes={constructionTypes}
                    refreshOtherTableHandler={refreshWithFilterReset}
                    workOrderChangeOtherPriceId={selectedWorkOrderChangeOtherPriceId}
                    costRounding={costRounding}
                    isReadOnly={isViewOtherModalOpened}
                />
            )}

            {isEditNoteModalOpened && (
                <EditNoteModal
                    data={rowData}
                    isOpened={isEditNoteModalOpened}
                    closeModalHandler={closeEditNoteModalHandler}
                    refreshTable={refreshWithFilterReset}
                />
            )}

            {isEditDistributionOpened && (
                /* @ts-ignore */
                <EditWorkOrderChangeOtherDistribution
                    workOrderChangeId={workOrderChangeId}
                    workOrderChangeOtherPriceId={selectedWorkOrderChangeOtherPriceId}
                    workOrderOtherPriceId={selectedWorkOrderOtherPriceId}
                    currentConstructionTypeId={constructionTypeId}
                    closeEditHandler={closeEditDistributionModalHandler}
                    refreshOtherTableHandler={handleGetWorkOrderChangeOtherPrice}
                    costRounding={costRounding}
                />
            )}
            <DeleteItemFrom
                isOpened={isSingleDeleteModalOpened}
                closeDeleteModal={closeSingleDeleteModalHandler}
                deleteData={deleteData}
                deleteStatus={deleteStatus}
                deleteError={deleteError}
                deleteSetData={deleteSetData}
                handleDelete={handleDelete}
                handleSuccess={singleDeleteSuccess}
                modalTextHeader={removeModalTitle}
                modalTextConfirmation={removeModalText}
                modalCancelButtonText={removeModalCancelBtn}
                modalConfirmButtonText={removeModalConfirmBtn}
            />
        </>
    );
});

WorkOrderChangeOtherPrice.displayName = 'WorkOrderChangeOtherPrice';
